import {
  getPolicyStatusLabel,
  isPolicyAnalyzingStatus,
  isPolicyApprovedStatus,
  isPolicyAwaitingReviewStatus,
  isPolicyRejectedStatus,
} from "@cle/models/policyStatus";
import ClePagination from "./ClePagination";
import CleTable, { type CleTableColumn } from "./CleTable";
import type { InsurancePolicyDocumentModel } from "../models/insurancePolicy";

type InsurancePoliciesTableProps = {
  policies: InsurancePolicyDocumentModel[];
  isError?: boolean;
  isLoading?: boolean;
  onPolicyClick?: (policyId: string) => void;
  pagination?: {
    isDisabled?: boolean;
    onPageChange: (page: number) => void;
    page: number;
    total: number;
    totalPages: number;
  };
  showStatusColumn?: boolean;
};

const MAX_FILE_NAME_LENGTH = 30;

const currencyFormatter = new Intl.NumberFormat("da-DK", {
  style: "currency",
  currency: "DKK",
  maximumFractionDigits: 0,
});

const truncateFileName = (fileName: string): string => {
  if (fileName.length <= MAX_FILE_NAME_LENGTH) {
    return fileName;
  }

  return `${fileName.slice(0, MAX_FILE_NAME_LENGTH)}...`;
};

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) {
    return "Ikke angivet";
  }

  return currencyFormatter.format(amount);
};

const formatDate = (date: string | null | undefined): string => {
  if (!date) {
    return "Ikke angivet";
  }

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "Ikke angivet";
  }

  return parsed.toLocaleDateString("da-DK");
};

const getStatusBadgeClass = (policy: InsurancePolicyDocumentModel) => {
  if (policy.reported) {
    return "cle-status-badge--reported";
  }

  if (isPolicyApprovedStatus(policy.status)) {
    return "cle-status-badge--success";
  }

  if (isPolicyAwaitingReviewStatus(policy.status)) {
    return "cle-status-badge--warning";
  }

  if (isPolicyRejectedStatus(policy.status)) {
    return "cle-status-badge--danger";
  }

  if (policy.status === "failed") {
    return "cle-status-badge--danger";
  }

  return "cle-status-badge--info";
};

const renderAnalyzingPlaceholder = (className: string) => (
  <span className={`placeholder rounded ${className}`} />
);

const getPolicyRowId = (policy: InsurancePolicyDocumentModel): string =>
  policy.documentId ?? policy.id;

const InsurancePoliciesTable = ({
  policies,
  isError = false,
  isLoading = false,
  onPolicyClick,
  pagination,
  showStatusColumn = true,
}: InsurancePoliciesTableProps) => {
  const columns: CleTableColumn<InsurancePolicyDocumentModel>[] = [
    {
      id: "fileName",
      header: "Filnavn",
      title: (policy) => policy.fileName,
      cell: (policy) => truncateFileName(policy.fileName),
    },
    {
      id: "insuranceCompany",
      header: "Forsikringsselskab",
      cell: (policy) =>
        isPolicyAnalyzingStatus(policy.status)
          ? renderAnalyzingPlaceholder("col-6")
          : (policy.insuranceCompany ?? "Ikke angivet"),
    },
    {
      id: "policyStartDate",
      header: "Police startdato",
      cell: (policy) =>
        isPolicyAnalyzingStatus(policy.status)
          ? renderAnalyzingPlaceholder("col-6")
          : formatDate(policy.policyStartDate),
    },
    {
      id: "lawyerLiabilityAmount",
      header: "Advokatansvar",
      cell: (policy) =>
        isPolicyAnalyzingStatus(policy.status)
          ? renderAnalyzingPlaceholder("col-5")
          : formatCurrency(policy.lawyerLiabilityAmount),
    },
    {
      id: "guaranteeAmount",
      header: "Garantibeløb",
      cell: (policy) =>
        isPolicyAnalyzingStatus(policy.status)
          ? renderAnalyzingPlaceholder("col-5")
          : formatCurrency(policy.guaranteeAmount),
    },
    ...(showStatusColumn
      ? [
          {
            id: "status",
            header: "Status",
            cell: (policy: InsurancePolicyDocumentModel) => {
              const isAnalyzing = isPolicyAnalyzingStatus(policy.status);

              return (
                <span
                  style={{ fontSize: "0.75rem" }}
                  className={`cle-status-badge gap-2 ${getStatusBadgeClass(policy)}`}
                >
                  {isAnalyzing ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    />
                  ) : null}
                  {policy.reported
                    ? "Rapporteret"
                    : getPolicyStatusLabel(policy.status)}
                </span>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <CleTable
        columns={columns}
        isError={isError}
        isLoading={isLoading}
        errorMessage="Forsikringspolicer kunne ikke indlæses lige nu."
        emptyTitle="Ingen forsikring uploadet endnu"
        emptyDescription="Når du uploader et dokument, vises det her i oversigten."
        data={policies}
        getRowId={getPolicyRowId}
        getRowProps={(policy) => ({
          "aria-busy": isPolicyAnalyzingStatus(policy.status)
            ? true
            : undefined,
          className: isPolicyAnalyzingStatus(policy.status)
            ? "placeholder-glow"
            : undefined,
        })}
        rowAction={
          onPolicyClick
            ? {
                className: "",
                onClick: (policyId) => onPolicyClick(policyId),
              }
            : undefined
        }
      />
      {pagination != null && policies.length > 0 ? (
        <ClePagination
          ariaLabel="Forsikringspolicer pagination"
          isDisabled={pagination.isDisabled}
          itemLabel="forsikringspolicer"
          onPageChange={pagination.onPageChange}
          page={pagination.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
        />
      ) : null}
    </>
  );
};

export default InsurancePoliciesTable;
